import React from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faXmark,
    faIdCard,
    faClipboardList,
    faListCheck,
    faUser,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { RightToWorkCheckApi, RightToWorkCheckStatusId } from "../../services/ArtistService";
import { getRightToWorkDisplay } from "./RightToWorkStatus";

interface RightToWorkSessionReviewDetailsProps {
    check: RightToWorkCheckApi;
    formatSessionReviewDateTime: (value: string) => string;
}

/** Header pill / status card styling for a Right To Work status id. */
const getStatusStyles = (statusId: string) => {
    switch (statusId) {
        case RightToWorkCheckStatusId.Passed:
            return { pillModifier: "approved", valueModifier: "completed", icon: faCheck };
        case RightToWorkCheckStatusId.Failed:
        case RightToWorkCheckStatusId.Expired:
            return { pillModifier: "rejected", valueModifier: "expired", icon: faXmark };
        case RightToWorkCheckStatusId.Pending:
        case RightToWorkCheckStatusId.Superseded:
            return { pillModifier: "requires-review", valueModifier: "ongoing", icon: faTriangleExclamation };
        case RightToWorkCheckStatusId.NotRequired:
            return { pillModifier: "requested", valueModifier: "default", icon: faTriangleExclamation };
        default:
            return { pillModifier: "", valueModifier: "default", icon: faTriangleExclamation };
    }
};

const formatDate = (value: string | null | undefined) => {
    if (!value) {
        return "-";
    }
    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.format("DD MMM YYYY") : value;
};

const formatPropertyValue = (value: string | boolean | null | undefined): string => {
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (value === null || value === undefined) {
        return "Not Found";
    }

    const normalizedValue = value.toString().trim();
    return normalizedValue === "" ? "Not Found" : normalizedValue;
};

const formatPropertyDate = (value: string | null | undefined) => {
    const formattedValue = formatDate(value);
    return formattedValue === "-" ? "Not Found" : formattedValue;
};

const VerificationField: React.FC<{ label: string; value: string | boolean | null | undefined; isValid?: boolean }> = ({
    label,
    value,
    isValid = true,
}) => (
    <div className="col-12 col-lg-4">
        <div className="artist-profile__verification-check-item">
            <span
                className={`artist-profile__verification-check-icon ${
                    isValid ? "artist-profile__verification-check-icon--pass" : "artist-profile__verification-check-icon--review"
                }`}
            >
                <FontAwesomeIcon icon={isValid ? faCheck : faXmark} aria-hidden="true" />
            </span>
            <span>
                {label} ({formatPropertyValue(value)})
            </span>
        </div>
    </div>
);

const DetailCard: React.FC<{ title: string; icon: typeof faCheck; children: React.ReactNode }> = ({ title, icon, children }) => (
    <div className="artist-profile__verification-card">
        <div className="artist-profile__verification-card-header">
            <div className="artist-profile__verification-card-title">
                <span className="artist-profile__verification-card-icon-wrap">
                    <FontAwesomeIcon icon={icon} className="artist-profile__verification-card-icon" aria-hidden="true" />
                </span>
                <span>{title}</span>
            </div>
        </div>
        <div className="row artist-profile__verification-checks-row">{children}</div>
    </div>
);

/**
 * Renders the Right To Work session summary and verification checks for the
 * selected check returned by RightToWork/GetChecksByContactId.
 */
const RightToWorkSessionReviewDetails: React.FC<RightToWorkSessionReviewDetailsProps> = ({ check, formatSessionReviewDateTime }) => {
    const statusId = (check.statusId || "").toString();
    const statusLabel = check.status || getRightToWorkDisplay(statusId).label;
    const statusStyles = getStatusStyles(statusId);

    return (
        <>
            <div className="artist-profile__verification-session-review">
                <div className="artist-profile__verification-session-review-header">
                    <div>
                        <div className="artist-profile__verification-session-review-kicker">RIGHT TO WORK VERIFICATION</div>
                        <h3 className="artist-profile__verification-session-review-title">Session review</h3>
                    </div>
                    <span
                        className={`artist-profile__verification-session-review-pill ${
                            statusStyles.pillModifier ? `artist-profile__verification-session-review-pill--${statusStyles.pillModifier}` : ""
                        }`}
                    >
                        <FontAwesomeIcon icon={statusStyles.icon} aria-hidden="true" />
                        <span>{statusLabel}</span>
                    </span>
                </div>

                <div className="row artist-profile__verification-session-review-row">
                    <div className="col-12 col-lg-3">
                        <div className="artist-profile__verification-session-review-card">
                            <div className="artist-profile__verification-session-review-label">Session ID</div>
                            <div
                                className="artist-profile__verification-session-review-value artist-profile__verification-session-review-value--truncate"
                                title={check.providerReportReference || undefined}
                            >
                                {check.providerReportReference || "-"}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-3">
                        <div className="artist-profile__verification-session-review-card">
                            <div className="artist-profile__verification-session-review-label">User tracking ID</div>
                            <div className="artist-profile__verification-session-review-value">{check.contactId || "-"}</div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-3">
                        <div className="artist-profile__verification-session-review-card">
                            <div className="artist-profile__verification-session-review-label">Bio Consent</div>
                            <div className="artist-profile__verification-session-review-value artist-profile__verification-session-review-value--meta">
                                {formatSessionReviewDateTime(check.checkedOn || "")}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-3">
                        <div className="artist-profile__verification-session-review-card">
                            <div className="artist-profile__verification-session-review-label">Status</div>
                            <div
                                className={`artist-profile__verification-session-review-value artist-profile__verification-session-review-value--${statusStyles.valueModifier}`}
                            >
                                {statusLabel || "-"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="artist-profile__verification-subtitle">VERIFICATION CHECKS</div>
            <DetailCard title="Document" icon={faIdCard}>
                <VerificationField label="Check Method" value={check.checkMethod} isValid={Boolean(check.checkMethod)} />
                <VerificationField label="Document Type" value={check.documentType} isValid={Boolean(check.documentType)} />
                <VerificationField label="Document Expiry Date" value={formatPropertyDate(check.documentExpiryDate)} isValid={Boolean(check.documentExpiryDate)} />
            </DetailCard>
            <DetailCard title="Permission" icon={faClipboardList}>
                <VerificationField label="Permission Type" value={check.permissionType} isValid={Boolean(check.permissionType)} />
                <VerificationField
                    label="Permission Expiry Date"
                    value={formatPropertyDate(check.permissionExpiryDate)}
                    isValid={Boolean(check.permissionExpiryDate)}
                />
                <VerificationField
                    label="Follow-Up Due Date"
                    value={formatPropertyDate(check.followUpDueDate)}
                    isValid={Boolean(check.followUpDueDate)}
                />
            </DetailCard>
            <DetailCard title="Evidence" icon={faUser}>
                <VerificationField label="Photo Match Confirmed" value={check.photoMatchConfirmed} isValid={Boolean(check.photoMatchConfirmed)} />
                <VerificationField label="Evidence" value={check.hasEvidence} isValid={Boolean(check.hasEvidence)} />
            </DetailCard>
        </>
    );
};

export default RightToWorkSessionReviewDetails;
